import { useMemo, useRef, memo } from "react";
import { KonvaEventObject } from "konva/lib/Node";
import { Line } from "react-konva";
import { Vector2d } from "konva/lib/types";
import { pointsToFlatArray } from "@/features/sku-sections/lib/transform-coordinate";
import { polygonProps } from "../lib/polygon-props";
import { Line as LineShape } from "konva/lib/shapes/Line";

interface PolygonProps {
  points: Vector2d[];
  isHovered: boolean;
  onPointerOver: () => void;
  onPointerOut: () => void;
  onClick: (event: KonvaEventObject<MouseEvent>) => void;
  color?: string;
  isSelected?: boolean;
  hoverEnabled?: boolean;
  hoverVisible?: boolean;
}

const Polygon: React.FC<PolygonProps> = ({
  points,
  isHovered,
  onPointerOver,
  onPointerOut,
  onClick,
  color,
  isSelected,
  hoverEnabled = true,
  hoverVisible = true,
}) => {
  const shapeRef = useRef<LineShape | null>(null);
  const pointerDownRef = useRef<{ x: number; y: number } | null>(null);
  const CLICK_THRESHOLD = 5;

  const alpha = isSelected ? 0.35 : 0.25;
  const polygon = polygonProps();

  const handlePointerDown = (event: KonvaEventObject<PointerEvent>) => {
    if (event.evt.ctrlKey) {
      return;
    }

    const stage = event.target.getStage();
    const pointer = stage?.getPointerPosition();
    if (pointer) {
      pointerDownRef.current = { x: pointer.x, y: pointer.y };
    }
  };

  const handlePointerUp = (event: KonvaEventObject<PointerEvent>) => {
    if (!pointerDownRef.current) return;

    const stage = event.target.getStage();
    const pointer = stage?.getPointerPosition();
    if (!pointer) return;

    const dx = pointer.x - pointerDownRef.current.x;
    const dy = pointer.y - pointerDownRef.current.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance <= CLICK_THRESHOLD) {
      onClick(event as KonvaEventObject<MouseEvent>);
    }

    pointerDownRef.current = null;
  };

  const flatPoints = useMemo(() => pointsToFlatArray(points), [points]);

  const fillColor = () => {
    if (color) {
      if (isSelected || isHovered) {
        return "transparent";
      }

      // Add alpha to custom colors
      return color + "80"; // Add 50% alpha (80 in hex)
    }

    if (isSelected) {
      return polygon.fill().yellow();
    } else {
      return polygon.fill(alpha).blue();
    }
  };

  const strokeColor = () => {
    if (color) {
      return color;
    }

    if (isSelected || (isHovered && hoverVisible)) {
      return polygon.stroke().yellow();
    } else {
      return polygon.stroke().blue();
    }
  };

  const strokeWidth =
    isHovered && hoverVisible ? polygon.bold() : polygon.thin();

  const handlePointerEnter = (_e: KonvaEventObject<PointerEvent>) => {
    if (hoverEnabled) {
      shapeRef.current?.moveToTop();
    }
    onPointerOver();
  };

  const handlePointerLeave = (_e: KonvaEventObject<PointerEvent>) => {
    if (!isSelected) {
      shapeRef.current?.moveToBottom();
    }
    onPointerOut();
  };

  return (
    <Line
      ref={shapeRef}
      points={flatPoints}
      fill={fillColor()}
      stroke={strokeColor()}
      strokeWidth={strokeWidth}
      closed
      shadowForStrokeEnabled={false}
      name="camera-sku-section"
      onMouseDown={handlePointerDown}
      onMouseUp={handlePointerUp}
      onMouseEnter={handlePointerEnter}
      onMouseLeave={handlePointerLeave}
    />
  );
};

const MemoizedPolygon = memo(Polygon);
MemoizedPolygon.displayName = "Polygon";
export default MemoizedPolygon;
